'use client';

import React from 'react';
import { QuestionCardProps } from './interfaces';
import { QuestionDisplay } from './QuestionDisplay';
import { QuestionOptions } from './QuestionOptions';
import { QuestionOptionsReview } from './QuestionOptionsReview';

/**
 * Single Responsibility: Compose question display and options into a card
 * Open/Closed: Can be extended with new option types without modification
 * Dependency Inversion: Depends on abstractions (interfaces) not concrete implementations
 */
export function QuestionCard({
  question,
  questionType,
  questionIndex,
  quiz,
  handlers,
  showCorrectness = false,
  correctnessChecker,
  getOptionCorrectness, // Legacy prop for backward compatibility
  containerClassName = 'bg-surface-1 rounded-lg p-8 flex flex-col gap-6',
  innerContainerClassName = 'flex flex-col gap-6 bg-surface-2 rounded-lg p-8',
  isQuiz = false,
  correctAnswers = null,
  quizData,
  isQuestionSubmitted = false,
}: QuestionCardProps & {
  isQuiz?: boolean;
  correctAnswers?: string | string[] | boolean | null;
  quizData?: any;
  isQuestionSubmitted?: boolean;
}) {
  if (!questionType) {
    return null;
  }

  const isReviewMode = showCorrectness || !!correctnessChecker || !!getOptionCorrectness;
  const isInteractiveMode = !!handlers;

  // Use correctnessChecker if available, otherwise fall back to getOptionCorrectness
  const correctnessCheckFunction = correctnessChecker?.check || getOptionCorrectness;

  return (
    <div className={containerClassName}>
      <div className={innerContainerClassName}>
        <form className="w-full flex flex-col gap-6" id={`quiz-form-${question.metadata_uid}`}>
          <div key={question?.metadata_uid} className="flex flex-col">
            <QuestionDisplay
              question={question}
              questionIndex={questionIndex}
              questionType={questionType}
            />

            <div className="flex flex-col gap-4 text-text-default-strong mt-8">
              {isInteractiveMode && handlers ? (
                <QuestionOptions
                  question={question}
                  questionType={questionType}
                  questionIndex={questionIndex}
                  quiz={quiz}
                  handlers={handlers}
                  isQuiz={isQuiz}
                  correctAnswers={correctAnswers}
                  quizData={quizData}
                  isQuestionSubmitted={isQuestionSubmitted}
                />
              ) : (
                <QuestionOptionsReview
                  question={question}
                  questionType={questionType}
                  questionIndex={questionIndex}
                  quiz={quiz}
                  showCorrectness={showCorrectness}
                  getOptionCorrectness={correctnessCheckFunction}
                  correctAnswers={correctAnswers}
                />
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

